/**
 * Image optimization utilities using browser Canvas API
 */

interface OptimizationOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: 'webp' | 'jpeg' | 'png';
  maintainAspectRatio?: boolean;
}

/**
 * Load an image from URL or blob
 */
function loadImage(src: string | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => resolve(img);
    img.onerror = reject;
    
    if (typeof src === 'string') {
      img.src = src;
    } else {
      img.src = URL.createObjectURL(src);
    }
  });
}

/**
 * Calculate optimal dimensions while maintaining aspect ratio
 */
function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  const aspectRatio = originalWidth / originalHeight;
  
  let width = originalWidth;
  let height = originalHeight;
  
  // Scale down if needed
  if (width > maxWidth) {
    width = maxWidth;
    height = width / aspectRatio;
  }
  
  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }
  
  return { width: Math.round(width), height: Math.round(height) };
}

/**
 * Optimize an image file
 */
export async function optimizeImage(
  file: File,
  options: OptimizationOptions = {}
): Promise<Blob> {
  const {
    maxWidth = 1200,
    maxHeight = 1200,
    quality = 0.85,
    format = 'webp',
    maintainAspectRatio = true
  } = options;
  
  const img = await loadImage(file);
  
  // Calculate dimensions
  const dimensions = maintainAspectRatio
    ? calculateDimensions(img.width, img.height, maxWidth, maxHeight)
    : { width: maxWidth, height: maxHeight };
  
  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.width = dimensions.width;
  canvas.height = dimensions.height;
  
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');
  
  if (format === 'jpeg') {
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  } else {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }

  ctx.drawImage(img, 0, 0, dimensions.width, dimensions.height);
  
  // Convert to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      },
      `image/${format}`,
      quality
    );
  });
}

/**
 * Create a thumbnail from an image
 */
export async function createThumbnail(
  file: File,
  size: number = 150
): Promise<Blob> {
  return optimizeImage(file, {
    maxWidth: size,
    maxHeight: size,
    quality: 0.8,
    format: 'webp'
  });
}

/**
 * Get image dimensions
 */
export async function getImageDimensions(
  file: File
): Promise<{ width: number; height: number }> {
  const img = await loadImage(file);
  return { width: img.width, height: img.height };
}

/**
 * Check if image needs optimization
 */
export function shouldOptimizeImage(
  file: File,
  maxSizeKB: number = 500
): boolean {
  const sizeInKB = file.size / 1024;
  
  // Always optimize if larger than max size
  if (sizeInKB > maxSizeKB) return true;
  
  // Optimize certain formats
  const optimizableFormats = ['image/bmp', 'image/tiff', 'image/gif'];
  if (optimizableFormats.includes(file.type)) return true;
  
  // Don't optimize small SVGs
  if (file.type === 'image/svg+xml' && sizeInKB < 50) return false;
  
  return false;
}

/**
 * Smart image optimization based on file characteristics
 */
export async function smartOptimizeImage(file: File): Promise<Blob> {
  // Don't optimize SVGs
  if (file.type === 'image/svg+xml') return file;
  
  const dimensions = await getImageDimensions(file);
  
  // Logo optimization (smaller files, preserve quality)
  if (dimensions.width <= 500 && dimensions.height <= 500) {
    return optimizeImage(file, {
      maxWidth: 400,
      maxHeight: 400,
      quality: 0.9,
      format: 'png' // Better for logos with transparency
    });
  }
  
  // Large image optimization
  if (dimensions.width > 2000 || dimensions.height > 2000) {
    return optimizeImage(file, {
      maxWidth: 1600,
      maxHeight: 1600,
      quality: 0.85,
      format: 'webp'
    });
  }
  
  // Default optimization
  if (shouldOptimizeImage(file)) {
    return optimizeImage(file, {
      maxWidth: 1200,
      maxHeight: 1200,
      quality: 0.85,
      format: 'webp'
    });
  }
  
  return file;
}